import { useQuery } from "@apollo/client";
import { Alert, Button, Chip, Skeleton, Stack, Typography } from "@mui/material";
import { gql } from "generated-graphql";
import {
  Issue,
  IssueLevel,
  ReportableWasteCodesQuery,
  ReportableWasteCodesQueryVariables,
} from "generated-graphql/graphql";
import { ReactNode, useMemo } from "react";

import { StepSubHeader } from "../StepSubHeader";
import { RecommendationHint } from "./RecommendationHint";
import {
  computeFieldDrift,
  FieldDrift,
  normalizeWasteCodeInput,
  resolveChosenField,
  SavedWasteActivity,
  WasteActivityEdits,
  wasteCodesEmpty,
  wasteCodesEqual,
  WasteCodesRecommendation,
} from "./wasteActivitySelections";
import { WasteCodeChipInput } from "./WasteCodeChipInput";

/**
 * All-time union of the facility's reportable federal + state waste codes.
 *
 * Queried with `startDate: null, endDate: null, view: REPORTABLE` so it matches
 * exactly what the 8700-12 snapshot's fallback computes
 * (`gatherRcra870012Content.ts` uses `view: "REPORTABLE"` over an unbounded
 * window). This is intentionally NOT bound to the step's adjustable window
 * control; it always reflects the full reportable history.
 */
const REPORTABLE_WASTE_CODES = gql(`
  query ReportableWasteCodes($facilityId: ID!) {
    facility(id: $facilityId) {
      id
      wasteCodes(startDate: null, endDate: null, view: REPORTABLE) {
        federalWasteCodes
        stateWasteCodes
      }
    }
  }
`);

/**
 * The facility's all-time reportable waste-code recommendation. Lifted out of
 * the field component so the PARENT step owns the query and passes the
 * recommendation down as props — rather than the child fetching it and pushing
 * it back up through an effect. The parent uses this same value for the save
 * payload, so the saved recommendation stays exactly what the user was shown.
 *
 * `recommendation` is null while loading and on error, so the step keeps Save
 * disabled rather than capturing an empty aggregation as "the recommendation".
 */
export function useReportableWasteCodesRecommendation(facilityId: string): {
  recommendation: WasteCodesRecommendation | null;
  loading: boolean;
  error: boolean;
  refetch: () => void;
} {
  const { data, loading, error, refetch } = useQuery<
    ReportableWasteCodesQuery,
    ReportableWasteCodesQueryVariables
  >(REPORTABLE_WASTE_CODES, { variables: { facilityId } });

  const recommendation = useMemo<WasteCodesRecommendation | null>(() => {
    if (loading || error || !data) {
      return null;
    }
    return {
      federalWasteCodes: data.facility?.wasteCodes?.federalWasteCodes ?? [],
      stateWasteCodes: data.facility?.wasteCodes?.stateWasteCodes ?? [],
    };
  }, [loading, error, data]);

  return { recommendation, loading, error: Boolean(error), refetch: () => void refetch() };
}

/**
 * The specific codes the compliance engine flagged, gathered from the issues'
 * `metadata.invalidCodes` (uppercased for case-insensitive chip matching). The
 * validator carries the offending codes there so the UI can highlight exactly
 * which chips are bad, letting the message state the reason once instead of
 * repeating it per code. `metadata` is an untyped JSON scalar, so we narrow
 * defensively.
 */
function collectInvalidCodes(issues: Issue[]): Set<string> {
  const codes = new Set<string>();
  for (const issue of issues) {
    const list: unknown = issue.metadata?.invalidCodes;
    if (Array.isArray(list)) {
      for (const code of list) {
        if (typeof code === "string") {
          codes.add(code.trim().toUpperCase());
        }
      }
    }
  }
  return codes;
}

/**
 * One editable code list: the shared `WasteCodeChipInput` (also used by the
 * Filing Notifications dialogs) with the recommendation/drift caption beneath.
 * Codes the engine flagged render as red (error) chips so the helper text can
 * state the reason once rather than listing each code. Read-only mode swaps
 * the input for a plain chip list but keeps the same highlighting and
 * recommendation/drift caption, matching the read-only status selects.
 */
function CodeListField({
  label,
  codes,
  recommended,
  drift,
  issues,
  onChange,
  readOnly,
}: {
  label: string;
  codes: string[];
  recommended: string[];
  drift: FieldDrift;
  /** Live compliance-engine issues for this field (empty when clean). */
  issues: Issue[];
  onChange: (codes: string[]) => void;
  readOnly: boolean;
}) {
  const invalidCodes = collectInvalidCodes(issues);
  const isInvalid = (code: string) => invalidCodes.has(code.trim().toUpperCase());

  if (readOnly) {
    return (
      <Stack spacing={1}>
        <Typography variant="subtitle2">{label}</Typography>
        {codes.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No {label.toLowerCase()} reported.
          </Typography>
        ) : (
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {codes.map((code) => (
              <Chip
                key={code}
                label={code}
                size="small"
                color={isInvalid(code) ? "error" : "default"}
              />
            ))}
          </Stack>
        )}
        <RecommendationHint
          recommendationText={recommended.length > 0 ? recommended.join(", ") : null}
          drift={drift}
          onAccept={() => onChange(recommended)}
          readOnly={readOnly}
        />
      </Stack>
    );
  }

  // Live CE issues render as the field's helper text (the app's inline-issue
  // idiom: `useValidatingForm` joins issue messages into the field error the
  // same way) — error styling for ERROR/CRITICAL, plain caption otherwise.
  // They never block Save; the mutation no longer rejects on CE issues.
  const issueText = issues.map((issue) => issue.message).join(" | ");
  const hasErrorIssue = issues.some(
    (issue) => issue.level === IssueLevel.Error || issue.level === IssueLevel.Critical
  );

  return (
    <Stack spacing={0.5}>
      <WasteCodeChipInput
        label={label}
        codes={codes}
        onChange={(next) => onChange(normalizeWasteCodeInput(next))}
        invalidCodes={[...invalidCodes]}
        error={hasErrorIssue}
        helperText={issueText || "Press Enter after each code"}
        size="small"
        placeholder="Add code"
        // Taller than a single line so wrapped chips have room before the
        // input grows, and chips top-align rather than centering in a tall box.
        textFieldSx={{ "& .MuiInputBase-root": { minHeight: 72, alignContent: "flex-start" } }}
      />
      <RecommendationHint
        recommendationText={recommended.length > 0 ? recommended.join(", ") : null}
        drift={drift}
        onAccept={() => onChange(recommended)}
        readOnly={readOnly}
      />
    </Stack>
  );
}

/**
 * Waste codes for the Waste Activity step. Presentational: the parent step owns
 * the reportable-codes query (via `useReportableWasteCodesRecommendation`) and
 * passes the recommendation (plus its loading/error state) down. Prefilled from
 * the saved `SubmissionWasteActivity` row when one exists and from that
 * recommendation otherwise.
 */
export function ReportableWasteCodes({
  saved,
  edits,
  issues,
  onEdit,
  readOnly,
  recommendedFederal,
  recommendedState,
  loading,
  error,
  onRetry,
}: {
  saved: SavedWasteActivity | null;
  edits: WasteActivityEdits;
  /**
   * Live compliance-engine issues for the chosen codes (from
   * `useWasteActivityCodeValidation` in the step); keyed by the schema's field
   * names so they split per field below.
   */
  issues: Issue[];
  onEdit: (edit: WasteActivityEdits) => void;
  readOnly: boolean;
  /** All-time reportable recommendation, owned by the parent step. */
  recommendedFederal: string[];
  recommendedState: string[];
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}) {
  const hasSaved = saved != null;
  const chosenFederal =
    resolveChosenField(
      edits.federalWasteCodes,
      saved?.federalWasteCodes ?? null,
      hasSaved,
      recommendedFederal
    ) ?? [];
  const chosenState =
    resolveChosenField(
      edits.stateWasteCodes,
      saved?.stateWasteCodes ?? null,
      hasSaved,
      recommendedState
    ) ?? [];

  // `isEmpty: wasteCodesEmpty` — an empty recommendation is "no
  // recommendation" for code lists, so chosen codes never read as an override
  // of it (consistent with the null-status case).
  const federalDrift = computeFieldDrift({
    chosen: chosenFederal,
    savedRecommended: saved?.recommendedFederalWasteCodes ?? null,
    liveRecommended: recommendedFederal,
    hasSaved,
    equals: wasteCodesEqual,
    isEmpty: wasteCodesEmpty,
  });
  const stateDrift = computeFieldDrift({
    chosen: chosenState,
    savedRecommended: saved?.recommendedStateWasteCodes ?? null,
    liveRecommended: recommendedState,
    hasSaved,
    equals: wasteCodesEqual,
    isEmpty: wasteCodesEmpty,
  });

  const federalIssues = issues.filter((issue) => issue.key === "federalWasteCodes");
  const stateIssues = issues.filter((issue) => issue.key === "stateWasteCodes");

  // Three-way body without nested ternaries. On error the code fields are
  // replaced by a retriable alert: the parent reports no recommendation while
  // in error (see the hook), so the step's Save stays disabled until a retry
  // succeeds rather than silently offering empty code lists.
  let body: ReactNode;
  if (loading) {
    body = <Skeleton height={120} variant="rounded" />;
  } else if (error) {
    body = (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={onRetry}>
            Retry
          </Button>
        }
      >
        Couldn&apos;t load the facility&apos;s reportable waste codes.
      </Alert>
    );
  } else {
    // Federal then state, vertically stacked with full-width inputs (matching
    // the Generator Status section). Extra spacing so the sections don't crowd
    // when both carry a recommendation/drift caption and inline issue text.
    body = (
      <Stack spacing={4}>
        <CodeListField
          label="Federal waste codes"
          codes={chosenFederal}
          recommended={recommendedFederal}
          drift={federalDrift}
          issues={federalIssues}
          onChange={(codes) => onEdit({ federalWasteCodes: codes })}
          readOnly={readOnly}
        />
        <CodeListField
          label="State waste codes"
          codes={chosenState}
          recommended={recommendedState}
          drift={stateDrift}
          issues={stateIssues}
          onChange={(codes) => onEdit({ stateWasteCodes: codes })}
          readOnly={readOnly}
        />
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <StepSubHeader title="Reportable Waste Codes" />
      <Typography variant="caption" color="text.secondary">
        The federal and state waste codes filed with this submission. Prefilled from the
        facility&apos;s all-time reportable codes; not affected by the activity window below.
      </Typography>
      {body}
    </Stack>
  );
}
